/**
 * Simplified Ebbinghaus / SM-2 spaced-repetition scheduler.
 *
 * Each card tracks: interval (days), ease factor, repetition count, lapses,
 * last review timestamp, and next due timestamp.
 *
 * Quality scale on review:
 *   0 = "forgot"          → reset interval to 1d, ease -= 0.20 (min 1.3)
 *   1 = "hard"            → interval *= 1.2, ease -= 0.15
 *   2 = "good"            → interval *= ease
 *   3 = "easy"            → interval *= ease * 1.3, ease += 0.10
 *
 * New cards start at interval = 0, ease = 2.5.
 */
import type { SrsCard } from './types';

export type Quality = 0 | 1 | 2 | 3;

export const QUALITY_LABEL: Record<Quality, { label: string; tone: string }> = {
  0: { label: '没记住', tone: 'text-ember-400' },
  1: { label: '有点难', tone: 'text-honey-300' },
  2: { label: '记住了', tone: 'text-mint-400' },
  3: { label: '很简单', tone: 'text-mint-300' },
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function newCard(wordId: string): SrsCard {
  return {
    wordId,
    intervalDays: 0,
    ease: 2.5,
    reps: 0,
    lapses: 0,
    lastReviewed: 0,
    nextDue: Date.now(),
  };
}

export function reviewCard(card: SrsCard, q: Quality): SrsCard {
  const now = Date.now();
  let { intervalDays, ease, reps, lapses } = card;

  if (q === 0) {
    intervalDays = 1;
    ease = Math.max(1.3, ease - 0.2);
    lapses += 1;
    reps = 0;
  } else if (q === 1) {
    intervalDays = Math.max(1, Math.round(intervalDays * 1.2));
    ease = Math.max(1.3, ease - 0.15);
    reps += 1;
  } else if (q === 2) {
    intervalDays = reps === 0 ? 1 : reps === 1 ? 3 : Math.round(intervalDays * ease);
    reps += 1;
  } else {
    intervalDays = reps === 0 ? 2 : reps === 1 ? 5 : Math.round(intervalDays * ease * 1.3);
    ease = Math.min(3.0, ease + 0.1);
    reps += 1;
  }

  return {
    ...card,
    intervalDays,
    ease,
    reps,
    lapses,
    lastReviewed: now,
    nextDue: now + intervalDays * MS_PER_DAY,
  };
}

export function isDue(card: SrsCard, now = Date.now()): boolean {
  return card.nextDue <= now;
}

export function daysUntilDue(card: SrsCard, now = Date.now()): number {
  return Math.round((card.nextDue - now) / MS_PER_DAY);
}
